import axios from 'axios';
import Data from '../Data/Data.json';
import { Crypter } from '../Functions/Security/Crypter';

export interface ActifDocumentType {
    TypeCodeDoc: string;
    LibelleDefault: string;
}

const valueOf = (row: any, ...keys: string[]) => {
    const key = keys.find(candidate => row?.[candidate] !== undefined && row?.[candidate] !== null);
    return key ? row[key] : '';
};

const extractRows = (result: any): any[] => {
    const candidate = result?.Result ?? result;
    const rows = candidate?.Table ?? candidate?.Table1 ?? candidate?.Rows ?? candidate;
    if (Array.isArray(rows)) return rows;
    return rows && typeof rows === 'object' ? [rows] : [];
};

export const loadActifDocumentTypes = async (authorization: string): Promise<ActifDocumentType[]> => {
    try {
        // Essayer d'abord l'endpoint GetTypeDocument
        const response = await axios.get(Data.ApiUrl.GetTypeDocument, {
            headers: {
                Authorization: authorization,
                CypherSearch: Crypter.encrypt('|ALL')
            }
        });
        const result = JSON.parse(Crypter.decrypt(response.data)).Value;
        const rows = extractRows(result);

        const mapped = rows
            .map((row: any) => ({
                TypeCodeDoc: String(valueOf(row, 'TypeCodeDoc', 'TYPECODEDOC', 'CodeTypeDoc', 'CODETYPEDOC', 'CodeTypeDocument', 'CODETYPEDOCUMENT', 'Code')),
                LibelleDefault: String(valueOf(row, 'LibelleDefault', 'LIBELLEDEFAULT', 'LibelleDefaultDoc', 'LIBELLEDEFAULTDOC', 'LibelleDoc', 'LIBELLEDOC', 'Libelle', 'LIBELLE', 'Description'))
            }))
            .filter((row: ActifDocumentType) => row.TypeCodeDoc && row.LibelleDefault);
        
        // Dédupliquer par TypeCodeDoc pour éviter les doublons
        return mapped.filter((type, index, self) => 
            index === self.findIndex(t => t.TypeCodeDoc === type.TypeCodeDoc)
        );
    } catch (error) {
        // Si GetTypeDocument échoue (procédure stockée manquante), utiliser AnySearch comme fallback
        console.warn('GetTypeDocument failed, falling back to AnySearch:', error);
        const response = await axios.get(Data.ApiUrl.AnySearch, {
            headers: {
                Authorization: authorization,
                CypherSearch: Crypter.encrypt(JSON.stringify({
                    Table: "TypeDocument",
                    Colonne1: "TypeCodeDoc",
                    Colonne2: "LibelleDefault",
                    Choix: 0
                }))
            }
        });
        const result = JSON.parse(Crypter.decrypt(response.data)).Value;
        const rows = extractRows(result);

        const mapped = rows
            .map((row: any) => ({
                TypeCodeDoc: String(valueOf(row, 'TypeCodeDoc', 'TYPECODEDOC', 'CodeTypeDoc', 'CODETYPEDOC', 'CodeTypeDocument', 'CODETYPEDOCUMENT', 'Code')),
                LibelleDefault: String(valueOf(row, 'LibelleDefault', 'LIBELLEDEFAULT', 'LibelleDefaultDoc', 'LIBELLEDEFAULTDOC', 'LibelleDoc', 'LIBELLEDOC', 'Libelle', 'LIBELLE', 'Description'))
            }))
            .filter((row: ActifDocumentType) => row.TypeCodeDoc && row.LibelleDefault);
        
        // Dédupliquer par TypeCodeDoc pour éviter les doublons
        return mapped.filter((type, index, self) => 
            index === self.findIndex(t => t.TypeCodeDoc === type.TypeCodeDoc)
        );
    }
};

export const getActifDocumentLabel = (document: any, definitions: ActifDocumentType[]) => {
    const code = document?.TypeCodeDoc || document?.TYPECODEDOC;
    return document?.LibelleDefault || document?.LIBELLEDEFAULT || document?.LibelleDoc || document?.LIBELLEDOC || document?.LibelleTypeDoc || document?.LIBELLETYPEDOC || definitions.find(type => type.TypeCodeDoc === code)?.LibelleDefault || code || 'Document';
};

const MIME_SIGNATURES: { prefix: string; mime: string }[] = [
    { prefix: '/9j/', mime: 'image/jpeg' },
    { prefix: 'iVBORw0KGgo', mime: 'image/png' },
    { prefix: 'R0lGOD', mime: 'image/gif' },
    { prefix: 'UklGR', mime: 'image/webp' },
    { prefix: 'JVBER', mime: 'application/pdf' },
    { prefix: 'UEsDB', mime: 'application/zip' },
    { prefix: 'SUQz', mime: 'audio/mpeg' }
];

export const sniffMimeFromBase64 = (content: string): string => {
    const signature = MIME_SIGNATURES.find(entry => content.startsWith(entry.prefix));
    return signature?.mime || '';
};

export const toDocumentDataUri = (document: any): string | null => {
    const raw = document?.Valeur ?? document?.VALEUR ?? document?.ContenuFichier ?? document?.CONTENUFICHIER ?? null;
    if (!raw || typeof raw !== 'string') return null;
    if (raw.startsWith('data:')) return raw;
    const declared = document?.TypeMime || document?.TYPEMIME || '';
    const sniffed = sniffMimeFromBase64(raw);
    const mime = sniffed || (declared && declared !== 'application/octet-stream' ? declared : 'application/octet-stream');
    return `data:${mime};base64,${raw}`;
};

export const extractActifDocumentRows = (result: any, codeActif?: string): any[] => {
    const tables: any[][] = [];
    const collect = (value: any) => {
        if (Array.isArray(value)) tables.push(value);
    };
    if (Array.isArray(result)) {
        if (result.every(Array.isArray)) result.forEach(collect);
        else collect(result);
    } else if (result && typeof result === 'object') {
        Object.keys(result).forEach(key => collect(result[key]));
    }

    const docTable = tables.find(rows => rows.some((row: any) =>
        row?.ContenuFichier != null || row?.CONTENUFICHIER != null || row?.CheminFichier != null || row?.Valeur != null || row?.VALEUR != null
    )) || [];

    if (!codeActif) return docTable;
    return docTable.filter((row: any) => {
        const code = row?.CodeActif ?? row?.CODEACTIF;
        return !code || String(code) === String(codeActif);
    });
};

export const mapActifDocumentsToPreview = (rows: any[], definitions: ActifDocumentType[]) =>
    rows.map((document: any) => ({
        label: getActifDocumentLabel(document, definitions),
        base64: toDocumentDataUri(document)
    }));

const sendTypeDocumentCommand = async (url: string, authorization: string, payload: any, method: 'post' | 'put' | 'delete') => {
    const response = await axios({
        method,
        url,
        data: JSON.stringify(Crypter.encrypt(JSON.stringify(payload))),
        headers: { Authorization: authorization, 'Content-Type': 'application/json' }
    });
    return JSON.parse(Crypter.decrypt(response.data)).Value;
};

export const createActifDocumentType = (authorization: string, payload: any) =>
    sendTypeDocumentCommand(Data.ApiUrl.CreateTypeDocument, authorization, payload, 'post');

export const updateActifDocumentType = (authorization: string, payload: any) =>
    sendTypeDocumentCommand(Data.ApiUrl.UpdateTypeDocument, authorization, payload, 'put');

export const deleteActifDocumentType = (authorization: string, payload: any) =>
    sendTypeDocumentCommand(Data.ApiUrl.DeleteTypeDocument, authorization, payload, 'delete');
